// pages/NotesPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Plus,
  FileText,
  Calendar,
  Key,
  Folder,
  Search,
  Filter,
  Star,
  Share2,
  Edit,
  Trash2,
  X,
  Save,
  Users,
  Clock,
  Tag,
  MoreVertical,
  CheckCircle2,
  AlertCircle,
  UserPlus,
  Lock,
  Globe
} from 'lucide-react';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';

interface Note {
  _id: string;
  title: string;
  content: string;
  projectId: string;
  teamId?: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  lastEditedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  template: 'blank' | 'meeting' | 'project-background' | 'key-resources' | 'weekly-planning';
  tags: string[];
  isFavorite: boolean;
  status: 'draft' | 'published' | 'archived';
  wordCount: number;
  lastEditedAt: string;
  sharedWith?: Array<{
    userId: {
      _id: string;
      name: string;
      email: string;
    };
    permission: 'read' | 'edit';
  }>;
  sharedWithTeams?: Array<{
    _id: string;
    name: string;
  }>;
}

type IconComponent = React.ComponentType<{ className?: string }>;

interface Template {
  id: 'blank' | 'meeting' | 'project-background' | 'key-resources' | 'weekly-planning';
  label: string;
  icon: IconComponent;
  description: string;
  content: string;
}

const templates: Template[] = [
  {
    id: 'meeting',
    label: 'Meeting notes',
    icon: Calendar,
    description: 'Take meeting notes',
    content: '# Meeting notes\n\n**Date:** \n**Participants:** \n\n## Agenda\n\n1. \n2. \n3. \n\n## Discussed points\n\n\n## Action items\n\n'
  },
  {
    id: 'project-background',
    label: 'Project background',
    icon: Folder,
    description: 'Contexte du projet',
    content: '# Contexte du projet\n\n## Objectifs\n\n\n## Contexte\n\n\n## Contraintes\n\n\n## Ressources disponibles\n\n'
  },
  {
    id: 'key-resources',
    label: 'Key resources',
    icon: Key,
    description: 'Key resources',
    content: '# Key resources\n\n## Documents\n\n\n## Useful links\n\n\n## Contacts\n\n\n## Tools\n\n'
  },
  {
    id: 'weekly-planning',
    label: 'Weekly planning',
    icon: Calendar,
    description: 'Planification hebdomadaire',
    content: '# Weekly planning\n\n## Goals for the week\n\n\n## Priority tasks\n\n\n## Scheduled meetings\n\n\n## Notes\n\n'
  },
  {
    id: 'blank',
    label: 'Blank note',
    icon: FileText,
    description: 'Note vierge',
    content: ''
  }
];

const NotesPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const user = useSelector((state: RootState) => selectUser(state));
  // Utiliser une URL relative pour que le proxy fonctionne, ou l'URL complète si spécifiée
  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const titleRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchNotes();
    }
  }, [projectId, filterTag]);

  // Ouvrir automatiquement la note spécifiée dans location.state
  useEffect(() => {
    const noteId = (location.state as any)?.noteId;
    if (noteId && notes.length > 0 && !selectedNote) {
      const noteToOpen = notes.find(note => note._id === noteId);
      if (noteToOpen) {
        setSelectedNote(noteToOpen);
        setEditingNote(noteToOpen);
        setIsCreating(false);
        // Nettoyer le state pour éviter de rouvrir la note à chaque re-render
        if (location.state) {
          window.history.replaceState({ ...location.state, noteId: undefined }, '');
        }
      }
    }
  }, [notes, location.state, selectedNote]);

  useEffect(() => {
    // Auto-save après 2 secondes d'inactivité
    if (editingNote && autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    if (editingNote) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleAutoSave();
      }, 2000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [editingNote?.title, editingNote?.content]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params: any = {};
      if (filterTag) params.tag = filterTag;
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API_URL}/notes/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setNotes(response.data.data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = (template?: Template) => {
    const newNote: Partial<Note> = {
      title: '',
      content: template?.content || '',
      template: template?.id || 'blank',
      tags: [],
      isFavorite: false,
      status: 'published'
    };

    setSelectedTemplate(template || null);
    setEditingNote(newNote as Note);
    setIsCreating(true);
    setSelectedNote(null);
    setShowTemplates(false);
    
    setTimeout(() => {
      titleRef.current?.focus();
    }, 100);
  };

  const handleSaveNote = async () => {
    if (!editingNote || !projectId) return;

    try {
      setSaving(true);
      setAutoSaveStatus('saving');
      const token = localStorage.getItem('token');

      if (isCreating) {
        const response = await axios.post(
          `${API_URL}/notes`,
          {
            title: editingNote.title || 'Sans titre',
            content: editingNote.content || '',
            projectId,
            template: editingNote.template || 'blank',
            tags: editingNote.tags || []
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setNotes([response.data.data, ...notes]);
          setSelectedNote(response.data.data);
          setIsCreating(false);
          setEditingNote(null);
          setAutoSaveStatus('saved');
        }
      } else {
        const response = await axios.put(
          `${API_URL}/notes/${editingNote._id}`,
          {
            title: editingNote.title,
            content: editingNote.content,
            tags: editingNote.tags,
            status: editingNote.status,
            isFavorite: editingNote.isFavorite
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setNotes(notes.map(n => n._id === editingNote._id ? response.data.data : n));
          setSelectedNote(response.data.data);
          setEditingNote(null);
          setAutoSaveStatus('saved');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setAutoSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoSave = async () => {
    if (!editingNote || !editingNote._id || isCreating) return;

    try {
      setAutoSaveStatus('saving');
      const token = localStorage.getItem('token');

      await axios.put(
        `${API_URL}/notes/${editingNote._id}`,
        {
          title: editingNote.title,
          content: editingNote.content
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAutoSaveStatus('saved');
      fetchNotes();
    } catch (error) {
      console.error('Erreur auto-save:', error);
      setAutoSaveStatus('error');
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/notes/${noteToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNotes(notes.filter(n => n._id !== noteToDelete._id));
      if (selectedNote?._id === noteToDelete._id) {
        setSelectedNote(null);
        setEditingNote(null);
      }
      setShowDeleteModal(false);
      setNoteToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const handleSelectNote = (note: Note) => {
    setSelectedNote(note);
    setEditingNote(note);
    setIsCreating(false);
  };

  const handleToggleFavorite = async (note: Note) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/notes/${note._id}`,
        { isFavorite: !note.isFavorite },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNotes(notes.map(n => n._id === note._id ? response.data.data : n));
        if (selectedNote?._id === note._id) {
          setSelectedNote(response.data.data);
        }
      }
    } catch (error) {
      console.error('Erreur lors du toggle favorite:', error);
    }
  };

  const filteredNotes = notes.filter(note => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        note.title.toLowerCase().includes(query) ||
        note.content.toLowerCase().includes(query) ||
        note.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const allTags = Array.from(new Set(notes.flatMap(note => note.tags)));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Chargement des notes...</div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-white">
      {/* Sidebar - Liste des notes */}
      <div className="w-80 border-r border-gray-200 flex flex-col">
        {/* Header avec recherche */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher des notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Filtres par tags */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterTag(null)}
                className={`px-2 py-1 text-xs rounded-full border ${
                  filterTag === null
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-gray-100 text-gray-700 border-gray-300'
                }`}
              >
                Tous
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setFilterTag(tag)}
                  className={`px-2 py-1 text-xs rounded-full border ${
                    filterTag === tag
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-gray-100 text-gray-700 border-gray-300'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Liste des notes */}
        <div className="flex-1 overflow-y-auto">
          {filteredNotes.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchQuery ? 'No note found' : 'No notes yet'}
            </div>
          ) : (
            <div className="p-2">
              {filteredNotes.map(note => (
                <div
                  key={note._id}
                  onClick={() => handleSelectNote(note)}
                  className={`p-3 mb-2 rounded-lg cursor-pointer transition-colors ${
                    selectedNote?._id === note._id
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-medium text-gray-900 truncate flex-1">
                      {note.title || 'Sans titre'}
                    </h3>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(note);
                      }}
                      className="ml-2"
                    >
                      <Star
                        className={`h-4 w-4 ${
                          note.isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                    {note.content.substring(0, 100)}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      {note.tags.slice(0, 2).map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-gray-200 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span>
                      {new Date(note.lastEditedAt).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bouton créer note */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Nouvelle note
          </button>
        </div>
      </div>

      {/* Zone principale d'édition */}
      <div className="flex-1 flex flex-col">
        {editingNote ? (
          <>
            {/* Toolbar */}
            <div className="border-b border-gray-200 p-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveNote}
                    disabled={saving}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Sauvegarde...' : 'Enregistrer'}
                  </button>
                  {selectedNote && (
                    <>
                      <button
                        onClick={() => {
                          setShowShareModal(true);
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Share2 className="h-4 w-4" />
                        Partager
                      </button>
                      <button
                        onClick={() => {
                          setNoteToDelete(selectedNote);
                          setShowDeleteModal(true);
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-100 flex items-center gap-2 text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                        Supprimer
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {autoSaveStatus === 'saving' && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Enregistrement...
                    </span>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      All changes are saved automatically
                    </span>
                  )}
                  {autoSaveStatus === 'error' && (
                    <span className="flex items-center gap-1 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      Erreur d'enregistrement
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setEditingNote(null);
                    setSelectedNote(null);
                    setIsCreating(false);
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Éditeur */}
            <div className="flex-1 overflow-y-auto p-8">
              <input
                ref={titleRef}
                type="text"
                placeholder="Ajouter un titre"
                value={editingNote.title || ''}
                onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                className="w-full text-3xl font-bold mb-6 border-none outline-none placeholder-gray-400"
              />
              <textarea
                ref={contentRef}
                placeholder="Start typing or press / for the menu"
                value={editingNote.content || ''}
                onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                className="w-full h-full min-h-[500px] text-gray-700 border-none outline-none resize-none placeholder-gray-400"
                style={{ fontFamily: 'inherit' }}
              />
            </div>
          </>
        ) : showTemplates ? (
          <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-2">Nouvelle note</h2>
              <p className="text-gray-600 mb-8">Choose a template to start</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map(template => {
                  const Icon = template.icon as IconComponent;
                  return (
                    <button
                      key={template.id}
                      onClick={() => handleCreateNote(template)}
                      className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all text-left"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className="h-6 w-6 text-blue-600" />
                        <h3 className="font-semibold text-gray-900">{template.label}</h3>
                      </div>
                      <p className="text-sm text-gray-600">{template.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Ajouter un titre</h2>
              <p className="text-gray-600 mb-6">Start typing or press / for the menu</p>
              <div className="flex flex-wrap justify-center gap-3">
                {templates.slice(0, 4).map(template => {
                  const Icon = template.icon as IconComponent;
                  return (
                    <button
                      key={template.id}
                      onClick={() => handleCreateNote(template)}
                      className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:border-blue-500 hover:shadow-md transition-all flex items-center gap-2"
                    >
                      <Icon className="h-5 w-5 text-blue-600" />
                      <span>{template.label}</span>
                    </button>
                  );
                })}
                <button
                  onClick={() => handleCreateNote(templates[4])}
                  className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:border-blue-500 hover:shadow-md transition-all flex items-center gap-2"
                >
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span>Note vierge</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de partage */}
      {showShareModal && selectedNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Partager "{selectedNote.title}"</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  This note is shared with:
                </p>
                <div className="space-y-2">
                  {selectedNote.sharedWith && selectedNote.sharedWith.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Utilisateurs :</p>
                      {selectedNote.sharedWith.map((share, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{share.userId.name} ({share.userId.email})</span>
                          <span className="text-xs text-gray-500">
                            {share.permission === 'edit' ? 'Edit' : 'View'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedNote.sharedWithTeams && selectedNote.sharedWithTeams.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Teams:</p>
                      {selectedNote.sharedWithTeams.map((team, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{team.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(!selectedNote.sharedWith || selectedNote.sharedWith.length === 0) &&
                   (!selectedNote.sharedWithTeams || selectedNote.sharedWithTeams.length === 0) && (
                    <p className="text-sm text-gray-500 italic">Aucun partage actuellement</p>
                  )}
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-3">
                  To share this note, use the sharing API. A user-picker will be added soon.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {showDeleteModal && noteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Supprimer la note</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{noteToDelete.title}"? This action is irreversible.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setNoteToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteNote}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesPage;

